import { Fragment, ReactNode } from "react";
import { Box, Link, Typography } from "@mui/material";

export type BotsCrumb = { label: string; onClick?: () => void };

export interface BotsTopBarProps {
  /** The client switcher - first crumb of every screen. */
  lead?: ReactNode;
  /** Links between the switcher and the current page. */
  crumbs?: BotsCrumb[];
  /** The current page, last and bold. */
  title?: ReactNode;
  titleText?: string;
  /** Facts about the current page, as compact pills. */
  chips?: ReactNode;
  actions?: ReactNode;
  /** Phone layout: crumbs, then title with its icon actions, then the pills. */
  stacked?: boolean;
}

const separator = (
  <Box component="span" aria-hidden sx={{ flex: "0 0 auto", fontSize: 16, color: "text.disabled" }}>
    /
  </Box>
);

/** Breadcrumb header of the Automations screens. */
export default function BotsTopBar({
  lead,
  crumbs = [],
  title,
  titleText,
  chips,
  actions,
  stacked = false,
}: BotsTopBarProps) {
  const crumbNodes = crumbs.map((crumb, index) => (
    <Fragment key={`${crumb.label}-${index}`}>
      {(lead || index > 0) && separator}
      {crumb.onClick ? (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={crumb.onClick}
          sx={{
            flex: "0 0 auto",
            fontSize: 16,
            fontWeight: 600,
            color: "primary.main",
            whiteSpace: "nowrap",
          }}
        >
          {crumb.label}
        </Link>
      ) : (
        <Typography
          component="span"
          sx={{ flex: "0 0 auto", fontSize: 16, fontWeight: 600, whiteSpace: "nowrap" }}
        >
          {crumb.label}
        </Typography>
      )}
    </Fragment>
  ));
  const titleNode = title ? (
    <Typography
      component="h1"
      title={titleText}
      sx={{
        flex: "0 1 auto",
        minWidth: 0,
        m: 0,
        fontSize: 16,
        fontWeight: 700,
        lineHeight: "22px",
        color: "text.primary",
        ...(stacked
          ? { py: "9px", overflowWrap: "anywhere" }
          : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }),
      }}
    >
      {title}
    </Typography>
  ) : null;
  const chipList = chips ? (
    <Box
      component="ul"
      aria-label="Settings"
      sx={{
        flex: "0 1 auto",
        minWidth: 0,
        maxWidth: "100%",
        listStyle: "none",
        m: 0,
        p: 0,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "6px",
        ...(stacked ? { mt: 0.5 } : {}),
      }}
    >
      {chips}
    </Box>
  ) : null;

  if (stacked) {
    return (
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          px: 2,
          pt: "10px",
          pb: "14px",
          bgcolor: "background.paper",
        }}
      >
        <Box
          component="nav"
          aria-label="Breadcrumb"
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          {lead}
          {crumbNodes}
        </Box>
        {(titleNode || actions) && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
            <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>{titleNode}</Box>
            {actions ? (
              <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5, mr: -1 }}>
                {actions}
              </Box>
            ) : null}
          </Box>
        )}
        {chipList}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        px: 3,
        py: "10px",
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          minHeight: 40,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: 2,
          rowGap: 1,
        }}
      >
        <Box
          sx={{
            flex: "0 1 auto",
            minWidth: 0,
            maxWidth: "100%",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            component="nav"
            aria-label="Breadcrumb"
            sx={{ flex: "0 1 auto", minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}
          >
            {lead}
            {crumbNodes}
            {titleNode && (lead || crumbs.length > 0) ? separator : null}
          </Box>
          {titleNode}
        </Box>
        {chipList}
      </Box>
      {actions ? (
        <Box
          sx={{
            flex: "0 0 auto",
            minHeight: 40,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {actions}
        </Box>
      ) : null}
    </Box>
  );
}
